/**
 * Source Documents Section for Accountant Report View
 * Displays downloadable financial documents (Tax Returns, P&L, Supporting Docs)
 */

import React from 'react';
import { apiFetch } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface DocumentFile {
  id?: string;
  filename?: string;
  url?: string;
}

interface SourceDocumentsSectionProps {
  reportId: string;
  clientName?: string;
  tax1065Files?: DocumentFile[];
  plFiles?: DocumentFile[];
  uploadedDocuments?: DocumentFile[];
  getAuthHeader: () => { Authorization: string };
}

// Reusable download handler using centralized API
const handleDownload = async (
  url: string,
  filename: string,
  getAuthHeader: () => { Authorization: string }
) => {
  try {
    const response = await apiFetch(url, { 
      headers: getAuthHeader(),
      showErrorToast: false
    });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  } catch (err) {
    console.error('Download failed:', err);
  }
};

// Document list component
interface DocumentListProps {
  files: DocumentFile[];
  reportId: string;
  docType: 'tax' | 'pl' | 'doc';
  colorClass: string;
  emptyMessage: string;
  getAuthHeader: () => { Authorization: string };
}

function DocumentList({ files, reportId, docType, colorClass, emptyMessage, getAuthHeader }: DocumentListProps) {
  if (!files || files.length === 0) {
    return <p className="text-sm text-gray-500 italic">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {files.map((file, idx) => {
        const downloadUrl = file.url || `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/documents/${docType}/${file.id || file.filename}`;
        const displayName = file.filename || `Document ${idx + 1}`;
        
        return (
          <a
            key={idx}
            href={downloadUrl}
            download={displayName}
            className={`flex items-center justify-between text-sm ${colorClass} p-2 rounded transition-colors cursor-pointer`}
            onClick={(e) => {
              e.preventDefault();
              handleDownload(downloadUrl, displayName, getAuthHeader);
            }}
          >
            <span className="truncate">{displayName}</span>
            <svg className="w-4 h-4 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
        );
      })}
    </div>
  );
}

export default function SourceDocumentsSection({
  reportId,
  clientName,
  tax1065Files = [],
  plFiles = [],
  uploadedDocuments = [],
  getAuthHeader
}: SourceDocumentsSectionProps) {
  const handleDownloadAll = async () => {
    try {
      const downloadUrl = `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/documents/download-all`;
      // Use centralized API utility for blob download
      const blob = await api.get(`/api/v1/collaboration/reports/${reportId}/documents/download-all`, {
        showErrorToast: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientName || 'Report'}_Documents_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download all documents:', error);
      console.error('Failed to download documents. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">📄</span>
        Source Financial Documents
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        All original documents uploaded and used in this calculation. Essential for court admissibility and forensic review.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Tax Returns */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="font-semibold text-blue-900">Tax Returns (1065 & K-1)</h3>
          </div>
          <DocumentList
            files={tax1065Files}
            reportId={reportId}
            docType="tax"
            colorClass="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
            emptyMessage="No tax documents uploaded"
            getAuthHeader={getAuthHeader}
          />
        </div>

        {/* P&L Statements */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="font-semibold text-green-900">P&L Statements</h3>
          </div>
          <DocumentList
            files={plFiles}
            reportId={reportId}
            docType="pl"
            colorClass="text-green-700 hover:text-green-900 hover:bg-green-100"
            emptyMessage="No P&L documents uploaded"
            getAuthHeader={getAuthHeader}
          />
          {plFiles && plFiles.length > 5 && (
            <p className="text-xs text-green-600 mt-2">Showing {plFiles.length} files</p>
          )}
        </div>

        {/* Supporting Documents */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h3 className="font-semibold text-purple-900">Supporting Docs</h3>
          </div>
          <DocumentList
            files={uploadedDocuments}
            reportId={reportId}
            docType="doc"
            colorClass="text-purple-700 hover:text-purple-900 hover:bg-purple-100"
            emptyMessage="No supporting documents"
            getAuthHeader={getAuthHeader}
          />
        </div>
      </div>

      <div className="mt-6 flex gap-4">
        <button 
          onClick={handleDownloadAll}
          className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center justify-center gap-2 cursor-pointer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download All Documents (ZIP)
        </button>
      </div>
    </div>
  );
}
